import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { UPDATE_PROJECT } from '../../graphql/mutations';
import { CreateProjectModal } from '../projects/CreateProjectModal';
import { showInkToast } from '../cms/InkToast';
import { AlwaysOnEditor } from './AlwaysOnEditor';
import {
  AlwaysOnConditions,
  APPROVE_CAMPAIGN,
  channelMark,
  channelName,
  dayTime,
  REACH_POST_FIELDS,
  ReachPost,
  postText,
  postTime,
  toDate,
  campaignRange,
  shortDate,
} from './reach';

/**
 * Reach → Campaigns ("tinycms UX optimization review v6", Prototype v6): the
 * dated campaigns and the always-on ones on the left; the selected one on the
 * right. Both are `projects`: a dated campaign has tactics the person approves
 * in one go; an always-on campaign fires on conditions (what the old automation
 * rules were) and can be paused, resumed or changed here.
 */

const REACH_CAMPAIGNS = gql`
  query ReachCampaigns($accountId: ID!) {
    projects(accountId: $accountId) {
      id
      name
      kind
      status
      deadline
      campaignStory
      landingPageUrl
      plan
      createdBy
      createdAt
      conditions
      channels {
        id
        platform
        profileName
        profileUsername
        approvalMode
      }
      recentPosts { ${REACH_POST_FIELDS} }
      posts {
        id
        title
        content
        status
        scheduledDate
        publishedAt
        targets {
          id
          platform
        }
      }
    }
  }
`;

interface CampaignPost {
  id: string;
  title: string;
  content: string;
  status: string;
  scheduledDate?: string | null;
  publishedAt?: string | null;
  targets: Array<{ id: string; platform: string }>;
}
interface CampaignChannel {
  id: string;
  platform: string;
  profileName?: string | null;
  profileUsername?: string | null;
  approvalMode?: string | null;
}
interface Campaign {
  id: string;
  name: string;
  kind: 'campaign' | 'always_on';
  status: string;
  deadline?: string | null;
  campaignStory?: string | null;
  landingPageUrl?: string | null;
  plan?: string | null;
  /** Null when the site's agent made it. */
  createdBy?: string | null;
  createdAt: string;
  conditions?: AlwaysOnConditions | null;
  channels: CampaignChannel[];
  recentPosts: ReachPost[];
  posts: CampaignPost[];
}

/**
 * Where a dated campaign is (Prototype v7): Ended once it's retired or its end
 * date has passed; Running while it's approved and on; otherwise Upcoming
 * (being planned or waiting for approval).
 */
type Phase = 'running' | 'upcoming' | 'ended';
const PHASES: Array<[Phase, string]> = [
  ['running', 'RUNNING'],
  ['upcoming', 'UPCOMING'],
  ['ended', 'ENDED'],
];
function phaseOf(c: Campaign, now = new Date()): Phase {
  const dayStart = new Date(now);
  dayStart.setHours(0, 0, 0, 0);
  const end = toDate(c.deadline);
  if (c.status === 'completed' || (end && end.getTime() < dayStart.getTime())) return 'ended';
  return c.status === 'active' ? 'running' : 'upcoming';
}
const WENT_OUT = new Set(['approved', 'scheduled', 'published']);
/** None of its posts was ever approved: an ended one reads "Not approved". */
const neverApproved = (c: Campaign) => !c.posts.some((p) => WENT_OUT.has(p.status));
const endTime = (c: Campaign) => toDate(c.deadline)?.getTime() ?? Infinity;

const POST_STATUS: Record<string, { label: string; color: string }> = {
  draft: { label: 'Idea', color: 'var(--ink-ink3)' },
  in_review: { label: 'Waiting on you', color: 'var(--ink-amber)' },
  approved: { label: 'Approved', color: 'var(--ink-accent)' },
  scheduled: { label: 'Scheduled', color: 'var(--ink-accent)' },
  published: { label: 'Posted', color: 'var(--ink-green-ink)' },
  cancelled: { label: 'Dropped', color: 'var(--ink-ink3)' },
};

const REACH_STATUS: Record<string, { label: string; color: string }> = {
  posted: { label: 'Posted', color: 'var(--ink-green-ink)' },
  publishing: { label: 'Posting', color: 'var(--ink-green-ink)' },
  scheduled: { label: 'Scheduled', color: 'var(--ink-accent)' },
  waiting: { label: 'Waiting on you', color: 'var(--ink-amber)' },
  skipped: { label: 'Skipped', color: 'var(--ink-ink3)' },
  draft: { label: 'Draft', color: 'var(--ink-ink3)' },
  missed: { label: 'Missed', color: 'var(--ink-red)' },
};

const CAMPAIGN_STATUS: Record<string, string> = {
  planning: 'Planning',
  review: 'In review',
  active: 'Running',
  completed: 'Finished',
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const POST_AS: Record<string, string> = {
  auto: 'links or text',
  post: 'links or text',
  link: 'links',
  text: 'text',
  photo: 'photos',
  video: 'videos',
  random: 'a mix of links and photos',
};

// "Jan 13", with the year when it isn't this year.
const day = (v?: string | null) => shortDate(v);
const hm = (v: string) => v.slice(0, 5);

/** The always-on conditions in words, for the facts rows. */
function describeConditions(k: AlwaysOnConditions | null | undefined) {
  const c = k || {};
  const topics = c.include?.length ? c.include.join(', ') : 'Every article';
  const but = c.exclude?.length ? ` (not ${c.exclude.join(', ')})` : '';
  const kinds = c.mediaTypes?.length ? ` · ${c.mediaTypes.join(', ')}` : '';
  const age = `newer than ${c.maxAgeHours ?? 24} hours`;
  const delay = c.delayHours ? `, ${c.delayHours}h after publishing` : '';
  const sched = c.schedule || [];
  let when = 'As soon as allowed';
  if (sched.some((e) => e.time))
    when = `At ${sched
      .map((e) => e.time)
      .filter(Boolean)
      .join(', ')}`;
  else if (sched.length === 1 && !sched[0].minuteMark)
    when = `Every ${sched[0].intervalMinutes || 60} minutes`;
  else if (sched.length)
    when = `At ${sched.map((e) => `:${String(e.minuteMark || 0).padStart(2, '0')}`).join(', ')} past each hour`;
  const windows = c.windows || [];
  let only = 'Any day, any time';
  if (windows.length) {
    const days = [...new Set(windows.map((w) => w.dayOfWeek))].sort().map((d) => DAYS[d]);
    const spans = [...new Set(windows.map((w) => `${hm(w.startTime)}–${hm(w.endTime)}`))];
    only = `${days.join(', ')} · ${spans.join(' or ')}`;
  }
  return {
    topics: `${topics}${but}${kinds}`,
    posts: `${POST_AS[c.postType || 'auto'] || c.postType} · articles ${age}${delay}`,
    when,
    only,
    legacy: Boolean(c.filterSql),
  };
}

export function ReachCampaigns() {
  const { accountId } = useAccount();
  const { data, refetch } = useQuery<{ projects: Campaign[] }>(REACH_CAMPAIGNS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const [approve] = useMutation<{
    approveCampaign: {
      approved: number;
      scheduled: number;
      undated: number;
      skippedChannels: string[];
    };
  }>(APPROVE_CAMPAIGN);
  const [updateProject] = useMutation(UPDATE_PROJECT);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Campaign | null | 'new'>(null);
  // ?campaign=<id> (Today's "Review") opens that campaign.
  const [params] = useSearchParams();
  const [sel, setSel] = useState<string | null>(params.get('campaign'));
  const [busy, setBusy] = useState(false);

  const all = useMemo(
    () =>
      [...(data?.projects || [])].sort(
        (a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0)
      ),
    [data]
  );
  // Dated campaigns by phase, empty groups left out: the soonest end first while
  // running or upcoming, the most recent end first once ended.
  const groups = useMemo(() => {
    const dated = all.filter((c) => c.kind !== 'always_on');
    return PHASES.map(([phase, label]) => ({
      phase,
      label,
      items: dated
        .filter((c) => phaseOf(c) === phase)
        .sort((a, b) => (phase === 'ended' ? endTime(b) - endTime(a) : endTime(a) - endTime(b))),
    })).filter((g) => g.items.length > 0);
  }, [all]);
  const campaigns = groups.flatMap((g) => g.items);
  const alwaysOn = all.filter((c) => c.kind === 'always_on');

  // Nothing picked: the first campaign, else the first always-on one.
  const current = sel ?? campaigns[0]?.id ?? alwaysOn[0]?.id ?? null;
  const campaign = all.find((c) => c.id === current) || null;

  const inReview = (c: Campaign) => c.posts.filter((p) => p.status === 'in_review');

  const approveCampaign = async (c: Campaign) => {
    setBusy(true);
    try {
      const { data: res } = await approve({ variables: { id: c.id } });
      await refetch();
      if (c.kind === 'always_on') {
        showInkToast(`${c.name} is on.`);
        return;
      }
      const r = res?.approveCampaign;
      const parts = [`Approved. ${r?.scheduled || 0} on the calendar.`];
      if (r?.undated)
        parts.push(`${r.undated} ${r.undated === 1 ? 'needs' : 'need'} a date before going out.`);
      if (r?.skippedChannels.length)
        parts.push(
          `${r.skippedChannels.map(channelName).join(', ')} posts need a photo or video, so those were left out.`
        );
      showInkToast(parts.join(' '));
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (c: Campaign, status: 'active' | 'paused' | 'completed') => {
    setBusy(true);
    try {
      await updateProject({ variables: { id: c.id, input: { status } } });
      await refetch();
      showInkToast(
        status === 'paused'
          ? `${c.name}: paused.`
          : status === 'active'
            ? `${c.name}: on again.`
            : `${c.name}: retired.`
      );
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(false);
    }
  };

  const alwaysOnTone = (c: Campaign) =>
    c.status === 'active'
      ? { dot: 'var(--ink-green)', color: 'var(--ink-green-ink)', label: 'Always on' }
      : c.status === 'planning'
        ? { dot: 'var(--ink-amber)', color: 'var(--ink-amber)', label: 'Not on yet' }
        : c.status === 'completed'
          ? { dot: 'var(--ink-line3)', color: 'var(--ink-ink3)', label: 'Retired' }
          : { dot: 'var(--ink-line3)', color: 'var(--ink-ink3)', label: 'Paused' };

  const approvalLine = (channels: CampaignChannel[]) => {
    const auto = channels
      .filter((c) => c.approvalMode === 'auto')
      .map((c) => channelName(c.platform));
    const ask = channels
      .filter((c) => c.approvalMode !== 'auto')
      .map((c) => channelName(c.platform));
    return [
      auto.length ? `${auto.join(', ')} ${auto.length === 1 ? 'posts' : 'post'} on its own.` : '',
      ask.length ? `${ask.join(', ')} ${ask.length === 1 ? 'asks' : 'ask'} you first.` : '',
    ]
      .filter(Boolean)
      .join(' ');
  };

  return (
    <div className="rcp">
      <aside className="rcp-rail">
        <button
          type="button"
          className="ink-btn ink-btn-quiet rcp-new"
          onClick={() => setCreating(true)}
        >
          New campaign
        </button>
        {groups.length ? (
          groups.map((g, gi) => (
            <React.Fragment key={g.phase}>
              <div className="pg-rail-head" style={gi ? { marginTop: 18 } : undefined}>
                {g.label}
              </div>
              {g.items.map((c) => {
                const ended = g.phase === 'ended';
                const waiting = !ended && inReview(c).length > 0;
                const notApproved = ended && neverApproved(c);
                return (
                  <button
                    key={c.id}
                    type="button"
                    className="pg-rail-item"
                    data-active={current === c.id ? 'true' : undefined}
                    onClick={() => setSel(c.id)}
                  >
                    <span
                      className="pg-dot"
                      style={{
                        background: waiting
                          ? 'var(--ink-amber)'
                          : g.phase === 'running'
                            ? 'var(--ink-green)'
                            : 'var(--ink-line3)',
                      }}
                    />
                    <span className="pg-rail-text">
                      <span className="pg-rail-title">{c.name}</span>
                      <span className="pg-rail-path">{campaignRange(c)}</span>
                    </span>
                    {waiting ? (
                      <span className="pg-rail-tag" style={{ color: 'var(--ink-amber)' }}>
                        Waiting
                      </span>
                    ) : notApproved ? (
                      <span className="pg-rail-tag" style={{ color: 'var(--ink-ink3)' }}>
                        Not approved
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </React.Fragment>
          ))
        ) : (
          <>
            <div className="pg-rail-head">CAMPAIGNS</div>
            <p className="rcp-none">No campaigns yet.</p>
          </>
        )}
        <div className="pg-rail-head rcp-rail-head-row" style={{ marginTop: 18 }}>
          ALWAYS ON
          <button
            type="button"
            className="ink-quiet-link"
            onClick={() => setEditing('new')}
            aria-label="New always-on campaign"
          >
            + New
          </button>
        </div>
        {alwaysOn.length ? (
          alwaysOn.map((c) => {
            const tone = alwaysOnTone(c);
            return (
              <button
                key={c.id}
                type="button"
                className="pg-rail-item"
                data-active={current === c.id ? 'true' : undefined}
                onClick={() => setSel(c.id)}
              >
                <span className="pg-dot" style={{ background: tone.dot }} />
                <span className="pg-rail-text">
                  <span className="pg-rail-title">{c.name}</span>
                  <span className="pg-rail-path">{tone.label}</span>
                </span>
                {c.status === 'planning' ? (
                  <span className="pg-rail-tag" style={{ color: 'var(--ink-amber)' }}>
                    Turn on
                  </span>
                ) : null}
              </button>
            );
          })
        ) : (
          <p className="rcp-none">
            Nothing always on. Set one up to share new articles as they’re published.
          </p>
        )}
      </aside>

      <section className="rcp-main">
        {campaign && campaign.kind === 'always_on' ? (
          <AlwaysOnDetail
            campaign={campaign}
            tone={alwaysOnTone(campaign)}
            approval={approvalLine(campaign.channels)}
            busy={busy}
            onTurnOn={() => void approveCampaign(campaign)}
            onPause={() => void setStatus(campaign, 'paused')}
            onResume={() => void setStatus(campaign, 'active')}
            onEdit={() => setEditing(campaign)}
          />
        ) : campaign ? (
          <>
            <h2 className="rcp-title">{campaign.name}</h2>
            <div className="rcp-meta">
              {inReview(campaign).length ? (
                <span style={{ color: 'var(--ink-amber)' }}>Waiting on you</span>
              ) : (
                <span>{CAMPAIGN_STATUS[campaign.status] || campaign.status}</span>
              )}
              {campaign.deadline ? <span>· Ends {day(campaign.deadline)}</span> : null}
              {!campaign.createdBy ? <span>· Drafted by your agent</span> : null}
            </div>
            {inReview(campaign).length ? (
              <div className="rcp-banner">
                <span className="ink-dot" style={{ background: 'var(--ink-amber)' }} />
                <div style={{ flex: 1 }}>
                  <strong>
                    {inReview(campaign).length}{' '}
                    {inReview(campaign).length === 1 ? 'tactic is' : 'tactics are'} waiting for your
                    approval
                  </strong>
                  <div className="ink-muted">
                    Approving puts them on the calendar to go out on their dates.
                  </div>
                </div>
                <button
                  type="button"
                  className="ink-btn ink-btn-primary"
                  disabled={busy}
                  onClick={() => void approveCampaign(campaign)}
                >
                  Approve campaign
                </button>
              </div>
            ) : null}
            {campaign.campaignStory ? <p className="rcp-story">{campaign.campaignStory}</p> : null}
            <div className="rcp-facts">
              {campaign.plan ? (
                <div>
                  <span>Plan</span>
                  <span style={{ whiteSpace: 'pre-wrap' }}>{campaign.plan}</span>
                </div>
              ) : null}
              {campaign.landingPageUrl ? (
                <div>
                  <span>Landing page</span>
                  <a href={campaign.landingPageUrl} target="_blank" rel="noreferrer">
                    {campaign.landingPageUrl.replace(/^https?:\/\//, '')} ↗
                  </a>
                </div>
              ) : null}
              <div>
                <span>Channels</span>
                <span>
                  {[
                    ...new Set(
                      campaign.posts
                        .filter((p) => p.status !== 'cancelled')
                        .flatMap((p) => p.targets.map((t) => channelName(t.platform)))
                    ),
                  ].join(', ') || 'None yet'}
                </span>
              </div>
              {campaign.deadline ? (
                <div>
                  <span>Ends</span>
                  <span>{day(campaign.deadline)}</span>
                </div>
              ) : null}
            </div>
            <div className="rcp-h3-row">
              <h3 className="rcp-h3">
                {campaign.posts.length} {campaign.posts.length === 1 ? 'tactic' : 'tactics'}
              </h3>
              <Link to="/reach/campaigns/manage" className="ink-quiet-link">
                Add or edit posts
              </Link>
            </div>
            {campaign.posts.length ? (
              <div className="rcp-posts">
                {[...campaign.posts]
                  .sort(
                    (a, b) =>
                      (toDate(a.scheduledDate)?.getTime() ?? Infinity) -
                      (toDate(b.scheduledDate)?.getTime() ?? Infinity)
                  )
                  .map((p) => {
                    const st = POST_STATUS[p.status] || POST_STATUS.draft;
                    const when = toDate(p.publishedAt) || toDate(p.scheduledDate);
                    return (
                      <div className="rcp-post" key={p.id}>
                        <span className="rcp-post-when">{when ? dayTime(when) : 'No date'}</span>
                        <span className="rcp-post-marks">
                          {p.targets.map((t) => (
                            <span
                              key={t.id}
                              className="rc-mark rc-mark-sm"
                              title={channelName(t.platform)}
                            >
                              {channelMark(t.platform)}
                            </span>
                          ))}
                        </span>
                        <span className="rcp-post-text">
                          <span className="rcp-post-idea">{p.title}</span>
                          {p.content && p.content !== p.title ? (
                            <span className="rcp-post-draft">{p.content}</span>
                          ) : null}
                        </span>
                        <span className="rcp-post-status" style={{ color: st.color }}>
                          {st.label}
                        </span>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <p className="ink-muted">
                No tactics yet. Ask your agent for ideas, or add posts yourself.
              </p>
            )}
          </>
        ) : (
          <div className="rc-empty">
            <div className="rc-empty-title">No campaigns yet</div>
            <p>
              A campaign groups the posts for one push, like a launch or a sale, with its own dates.
              An always-on campaign shares new articles as they’re published. Start one, or ask your
              agent to plan it.
            </p>
            <div className="rcp-actions">
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                onClick={() => setCreating(true)}
              >
                New campaign
              </button>
              <button type="button" className="ink-btn" onClick={() => setEditing('new')}>
                New always-on campaign
              </button>
            </div>
          </div>
        )}
      </section>

      <CreateProjectModal
        open={creating}
        onClose={() => setCreating(false)}
        onSubmit={() => void refetch()}
      />
      {editing ? (
        <AlwaysOnEditor
          campaign={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(id) => {
            setEditing(null);
            if (id) setSel(id);
            void refetch();
          }}
        />
      ) : null}
    </div>
  );
}

function AlwaysOnDetail({
  campaign,
  tone,
  approval,
  busy,
  onTurnOn,
  onPause,
  onResume,
  onEdit,
}: {
  campaign: Campaign;
  tone: { color: string; label: string };
  approval: string;
  busy: boolean;
  onTurnOn: () => void;
  onPause: () => void;
  onResume: () => void;
  onEdit: () => void;
}) {
  const facts = describeConditions(campaign.conditions);
  const posts = campaign.recentPosts || [];
  const posted = posts.filter((p) => p.reachStatus === 'posted').length;
  return (
    <>
      <h2 className="rcp-title">{campaign.name}</h2>
      <div className="rcp-meta">
        <span style={{ color: tone.color }}>{tone.label}</span>
        <span>
          · {campaign.createdBy ? 'Set up by you' : 'Proposed by your agent'} ·{' '}
          {day(campaign.createdAt)}
        </span>
      </div>
      {campaign.status === 'planning' ? (
        <div className="rcp-banner">
          <span className="ink-dot" style={{ background: 'var(--ink-amber)' }} />
          <div style={{ flex: 1 }}>
            <strong>Not on yet</strong>
            <div className="ink-muted">
              Nothing posts until you turn it on. Check the channels and conditions first.
            </div>
          </div>
          <button
            type="button"
            className="ink-btn ink-btn-primary"
            disabled={busy}
            onClick={onTurnOn}
          >
            Turn it on
          </button>
        </div>
      ) : null}
      {campaign.campaignStory ? <p className="rcp-story">{campaign.campaignStory}</p> : null}
      <div className="rcp-facts">
        <div>
          <span>Articles</span>
          <span>{facts.topics}</span>
        </div>
        <div>
          <span>Posts</span>
          <span>{facts.posts}</span>
        </div>
        <div>
          <span>When</span>
          <span>{facts.when}</span>
        </div>
        <div>
          <span>Only on</span>
          <span>{facts.only}</span>
        </div>
        <div>
          <span>Channels</span>
          <span>
            {campaign.channels.length
              ? campaign.channels
                  .map((c) => c.profileName || c.profileUsername || channelName(c.platform))
                  .join(', ')
              : 'None — pick one under Change'}
          </span>
        </div>
        <div>
          <span>Approval</span>
          <span>
            {approval || '—'}{' '}
            <Link to="/reach/channels" className="ink-quiet-link">
              Change
            </Link>
          </span>
        </div>
        {facts.legacy ? (
          <div>
            <span>Custom filter</span>
            <span className="ink-muted">A filter from the old rule still applies (legacy).</span>
          </div>
        ) : null}
      </div>
      <div className="rcp-actions" style={{ marginBottom: 26 }}>
        {campaign.status === 'active' ? (
          <button type="button" className="ink-btn ink-btn-quiet" disabled={busy} onClick={onPause}>
            Pause
          </button>
        ) : campaign.status === 'paused' ? (
          <button
            type="button"
            className="ink-btn ink-btn-primary"
            disabled={busy}
            onClick={onResume}
          >
            Resume
          </button>
        ) : null}
        <button type="button" className="ink-btn" disabled={busy} onClick={onEdit}>
          Change
        </button>
      </div>
      <div className="rcp-h3-row">
        <h3 className="rcp-h3">
          {posts.length ? `Last ${posts.length} ${posts.length === 1 ? 'post' : 'posts'}` : 'Posts'}
          {posted ? <span className="ink-muted"> · {posted} posted</span> : null}
        </h3>
        <Link to="/reach/calendar" className="ink-quiet-link">
          Open the calendar
        </Link>
      </div>
      {posts.length ? (
        <div className="rcp-posts">
          {posts.map((p) => {
            const st = REACH_STATUS[p.reachStatus] || REACH_STATUS.draft;
            const t = postTime(p);
            return (
              <div className="rcp-post" key={p.id}>
                <span className="rcp-post-when">{t ? dayTime(t) : 'No date'}</span>
                <span className="rcp-post-marks">
                  <span className="rc-mark rc-mark-sm" title={channelName(p.platform)}>
                    {channelMark(p.platform)}
                  </span>
                </span>
                <span className="rcp-post-text">
                  <span className="rcp-post-idea">{postText(p) || 'Untitled post'}</span>
                </span>
                <span className="rcp-post-status" style={{ color: st.color }}>
                  {st.label}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="ink-muted">
          {campaign.status === 'active'
            ? 'No posts yet. The next matching article gets one.'
            : 'No posts yet.'}
        </p>
      )}
    </>
  );
}
